import React from 'react';
import PropTypes from 'prop-types';
import './Toggle.scss';

/**
 * Reusable Toggle Component
 * A customizable toggle switch with label support
 *
 * @component
 * @example
 * ```jsx
 * <Toggle
 *   checked={isEnabled}
 *   onChange={(checked) => setIsEnabled(checked)}
 *   label="Enable Feature"
 *   title="Toggle this feature on or off"
 * />
 * ```
 */
const Toggle = ({
  checked = false,
  onChange,
  label,
  title,
  disabled = false,
  ariaLabel,
  className = '',
  hideLabel = false,
}) => {
  const handleChange = (e) => {
    if (!disabled && onChange) {
      onChange(e.target.checked);
    }
  };

  return (
    <label
      className={`toggle ${disabled ? 'toggle--disabled' : ''} ${className}`}
      title={title}
    >
      <input
        type='checkbox'
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        aria-label={ariaLabel || label}
        className='toggle__input'
      />
      <span className='toggle__slider'></span>
      {label && !hideLabel && <span className='toggle__label'>{label}</span>}
    </label>
  );
};

Toggle.propTypes = {
  /** Whether the toggle is checked */
  checked: PropTypes.bool,
  /** Callback fired when toggle state changes */
  onChange: PropTypes.func.isRequired,
  /** Label text to display next to toggle */
  label: PropTypes.string,
  /** Tooltip text on hover */
  title: PropTypes.string,
  /** Whether the toggle is disabled */
  disabled: PropTypes.bool,
  /** Accessibility label for screen readers */
  ariaLabel: PropTypes.string,
  /** Additional CSS classes */
  className: PropTypes.string,
  /** Hide the label (useful for mobile) */
  hideLabel: PropTypes.bool,
};

export default Toggle;
